import React from 'react';
import { RestoreOperation } from '../../types/api';
import { useTranslation } from 'react-i18next';

interface RestoreStatusTimelineProps {
  restore: RestoreOperation;
}

const RestoreStatusTimeline: React.FC<RestoreStatusTimelineProps> = ({ restore }) => {
  const { t } = (useTranslation as any)('backups');
  const steps = [
    {
      id: 'pending',
      label: t('restoreTimeline.initiated'),
      description: t('restoreTimeline.initiatedDesc')
    },
    {
      id: 'in_progress',
      label: t('restoreTimeline.clusterCreation'),
      description: t('restoreTimeline.clusterCreationDesc')
    },
    {
      id: 'awaiting_validation',
      label: t('restoreTimeline.awaitingValidation'),
      description: t('restoreTimeline.awaitingValidationDesc')
    },
    {
      id: 'completed',
      label: t('restoreTimeline.completed'),
      description: t('restoreTimeline.completedDesc')
    }
  ];

  const getStepStatus = (stepId: string): 'completed' | 'current' | 'pending' => {
    const stepOrder = ['pending', 'in_progress', 'awaiting_validation', 'completed'];
    const currentIndex = stepOrder.indexOf(restore.status);
    const thisStepIndex = stepOrder.indexOf(stepId);

    if (restore.status === 'failed' || restore.status === 'cancelled') {
      return thisStepIndex <= currentIndex ? 'completed' : 'pending';
    }

    // Si la restauration est terminée et c'est le step 'completed', le marquer comme terminé
    if (restore.status === 'completed' && stepId === 'completed') {
      return 'completed';
    }

    if (thisStepIndex < currentIndex) return 'completed';
    if (thisStepIndex === currentIndex) return 'current';
    return 'pending';
  };

  return (
    <div className="space-y-6">
      {steps.map((step, index) => {
        const status = getStepStatus(step.id);
        const isLast = index === steps.length - 1;

        return (
          <div key={step.id} className="relative">
            {/* Vertical line */}
            {!isLast && (
              <div
                className={`absolute left-6 top-12 bottom-0 w-0.5 ${
                  status === 'completed'
                    ? 'bg-green-500'
                    : status === 'current'
                    ? 'bg-accent-500'
                    : 'bg-surface-4'
                }`}
              />
            )}

            <div className="flex items-start gap-4">
              {/* Icon */}
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center z-10 ${
                  status === 'completed'
                    ? 'bg-green-500/12'
                    : status === 'current'
                    ? 'bg-accent-500/12 animate-pulse'
                    : 'bg-surface-3'
                }`}
              >
                {status === 'completed' ? (
                  <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : status === 'current' ? (
                  <div className="w-3 h-3 bg-accent-600 rounded-full"></div>
                ) : (
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 pt-2">
                <h3
                  className={`text-base font-semibold mb-1 ${
                    status === 'current'
                      ? 'text-accent-400'
                      : status === 'completed'
                      ? 'text-text-primary'
                      : 'text-text-secondary'
                  }`}
                >
                  {step.label}
                </h3>
                <p
                  className={`text-sm ${
                    status === 'current'
                      ? 'text-text-primary'
                      : 'text-text-secondary'
                  }`}
                >
                  {step.description}
                </p>

                {/* Status indicator */}
                {status === 'current' && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="w-2 h-2 bg-accent-500 rounded-full animate-pulse" />
                    <span className="text-xs text-blue-400 font-medium">
                      {t('restoreTimeline.inProgress')}
                    </span>
                  </div>
                )}
                {status === 'completed' && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-xs text-green-400 font-medium">
                      {t('restoreTimeline.done')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Error or Cancelled state */}
      {(restore.status === 'failed' || restore.status === 'cancelled') && (
        <div className="mt-6 p-4 bg-red-500/12 rounded-lg">
          <h4 className="text-sm font-semibold text-red-400 mb-2">
            {restore.status === 'failed' ? t('restoreTimeline.restoreFailed') as string : t('restoreTimeline.restoreCancelled') as string}
          </h4>
          {restore.error_message && (
            <p className="text-sm text-red-400">{restore.error_message}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default RestoreStatusTimeline;
